import { Check, CheckCircle2, ChevronDown, Loader2, Search, Send } from "lucide-react";
import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lang, t } from "../i18n";
import { getHongKongContactSeoDocument } from "../seoConfig";
import { leadTrackingKeys, reportLeadConversion, resolveConversionPlatform, sensitiveUrlQueryKeys } from "../tracking";
import { Footer } from "./Footer";
import { HomeNav } from "./home/HomeNav";
import { HongKongFooter } from "./home/HongKongFooter";
import "./home/home.css";
import { Nav } from "./Nav";
import { Seo } from "./Seo";

function joinApiUrl(baseUrl?: string, pathUrl?: string) {
  const base = (baseUrl || "").trim().replace(/\/+$/, "");
  const path = (pathUrl || "/api/v1/contact-us").trim();

  if (!base) return path;
  if (!path) return base;
  if (/^https?:\/\//i.test(path)) return path;

  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

const contactEndpoint = joinApiUrl(import.meta.env.VITE_API_BASE_URL, import.meta.env.VITE_CONTACT_API_URL);

type ContactUsResponse = {
  code?: number;
  msg?: string;
  data?: {
    submission_id?: string;
    received_at?: string;
  } | null;
};

declare global {
  interface Window {
    gtag_report_conversion?: (url?: string) => false;
    x_report_conversion?: () => void;
  }
}

function FieldLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <span className="mb-1.5 block text-[13px] font-extrabold text-[#2a2630]">
      {children}
      {required ? <span className="ml-1 text-[#e930c9]">*</span> : null}
    </span>
  );
}

function inputClass(hasError?: boolean) {
  return [
    "min-h-[44px] w-full rounded-xl border bg-white px-3.5 text-[14px] font-semibold text-[#100f12] outline-none transition-all",
    "placeholder:text-[#9a879b] focus:border-[#7b2fd0] focus:ring-4 focus:ring-[#7b2fd0]/10",
    hasError ? "border-[#ff7b9a] bg-[#fff7fa]" : "border-[#eaddea]",
  ].join(" ");
}

type PhoneCountryOption = {
  region: string;
  code: string;
};

const phoneCountryOptions: PhoneCountryOption[] = [
  { region: "AC", code: "+247" },
  { region: "AF", code: "+93" },
  { region: "AX", code: "+358" },
  { region: "AL", code: "+355" },
  { region: "DZ", code: "+213" },
  { region: "AS", code: "+1" },
  { region: "AD", code: "+376" },
  { region: "AO", code: "+244" },
  { region: "AI", code: "+1" },
  { region: "AG", code: "+1" },
  { region: "AR", code: "+54" },
  { region: "AM", code: "+374" },
  { region: "AW", code: "+297" },
  { region: "AU", code: "+61" },
  { region: "AT", code: "+43" },
  { region: "AZ", code: "+994" },
  { region: "BS", code: "+1" },
  { region: "BH", code: "+973" },
  { region: "BD", code: "+880" },
  { region: "BB", code: "+1" },
  { region: "BY", code: "+375" },
  { region: "BE", code: "+32" },
  { region: "BZ", code: "+501" },
  { region: "BJ", code: "+229" },
  { region: "BM", code: "+1" },
  { region: "BT", code: "+975" },
  { region: "BO", code: "+591" },
  { region: "BQ", code: "+599" },
  { region: "BA", code: "+387" },
  { region: "BW", code: "+267" },
  { region: "BR", code: "+55" },
  { region: "IO", code: "+246" },
  { region: "VG", code: "+1" },
  { region: "BN", code: "+673" },
  { region: "BG", code: "+359" },
  { region: "BF", code: "+226" },
  { region: "BI", code: "+257" },
  { region: "KH", code: "+855" },
  { region: "CM", code: "+237" },
  { region: "CA", code: "+1" },
  { region: "CV", code: "+238" },
  { region: "KY", code: "+1" },
  { region: "CF", code: "+236" },
  { region: "TD", code: "+235" },
  { region: "CL", code: "+56" },
  { region: "CN", code: "+86" },
  { region: "CX", code: "+61" },
  { region: "CC", code: "+61" },
  { region: "CO", code: "+57" },
  { region: "KM", code: "+269" },
  { region: "CG", code: "+242" },
  { region: "CD", code: "+243" },
  { region: "CK", code: "+682" },
  { region: "CR", code: "+506" },
  { region: "CI", code: "+225" },
  { region: "HR", code: "+385" },
  { region: "CU", code: "+53" },
  { region: "CW", code: "+599" },
  { region: "CY", code: "+357" },
  { region: "CZ", code: "+420" },
  { region: "DK", code: "+45" },
  { region: "DJ", code: "+253" },
  { region: "DM", code: "+1" },
  { region: "DO", code: "+1" },
  { region: "EC", code: "+593" },
  { region: "EG", code: "+20" },
  { region: "SV", code: "+503" },
  { region: "GQ", code: "+240" },
  { region: "ER", code: "+291" },
  { region: "EE", code: "+372" },
  { region: "SZ", code: "+268" },
  { region: "ET", code: "+251" },
  { region: "FK", code: "+500" },
  { region: "FO", code: "+298" },
  { region: "FJ", code: "+679" },
  { region: "FI", code: "+358" },
  { region: "FR", code: "+33" },
  { region: "GF", code: "+594" },
  { region: "PF", code: "+689" },
  { region: "GA", code: "+241" },
  { region: "GM", code: "+220" },
  { region: "GE", code: "+995" },
  { region: "DE", code: "+49" },
  { region: "GH", code: "+233" },
  { region: "GI", code: "+350" },
  { region: "GR", code: "+30" },
  { region: "GL", code: "+299" },
  { region: "GD", code: "+1" },
  { region: "GP", code: "+590" },
  { region: "GU", code: "+1" },
  { region: "GT", code: "+502" },
  { region: "GG", code: "+44" },
  { region: "GN", code: "+224" },
  { region: "GW", code: "+245" },
  { region: "GY", code: "+592" },
  { region: "HT", code: "+509" },
  { region: "HN", code: "+504" },
  { region: "HK", code: "+852" },
  { region: "HU", code: "+36" },
  { region: "IS", code: "+354" },
  { region: "IN", code: "+91" },
  { region: "ID", code: "+62" },
  { region: "IR", code: "+98" },
  { region: "IQ", code: "+964" },
  { region: "IE", code: "+353" },
  { region: "IM", code: "+44" },
  { region: "IL", code: "+972" },
  { region: "IT", code: "+39" },
  { region: "JM", code: "+1" },
  { region: "JP", code: "+81" },
  { region: "JE", code: "+44" },
  { region: "JO", code: "+962" },
  { region: "KZ", code: "+7" },
  { region: "KE", code: "+254" },
  { region: "KI", code: "+686" },
  { region: "XK", code: "+383" },
  { region: "KW", code: "+965" },
  { region: "KG", code: "+996" },
  { region: "LA", code: "+856" },
  { region: "LV", code: "+371" },
  { region: "LB", code: "+961" },
  { region: "LS", code: "+266" },
  { region: "LR", code: "+231" },
  { region: "LY", code: "+218" },
  { region: "LI", code: "+423" },
  { region: "LT", code: "+370" },
  { region: "LU", code: "+352" },
  { region: "MO", code: "+853" },
  { region: "MG", code: "+261" },
  { region: "MW", code: "+265" },
  { region: "MY", code: "+60" },
  { region: "MV", code: "+960" },
  { region: "ML", code: "+223" },
  { region: "MT", code: "+356" },
  { region: "MH", code: "+692" },
  { region: "MQ", code: "+596" },
  { region: "MR", code: "+222" },
  { region: "MU", code: "+230" },
  { region: "YT", code: "+262" },
  { region: "MX", code: "+52" },
  { region: "FM", code: "+691" },
  { region: "MD", code: "+373" },
  { region: "MC", code: "+377" },
  { region: "MN", code: "+976" },
  { region: "ME", code: "+382" },
  { region: "MS", code: "+1" },
  { region: "MA", code: "+212" },
  { region: "MZ", code: "+258" },
  { region: "MM", code: "+95" },
  { region: "NA", code: "+264" },
  { region: "NR", code: "+674" },
  { region: "NP", code: "+977" },
  { region: "NL", code: "+31" },
  { region: "NC", code: "+687" },
  { region: "NZ", code: "+64" },
  { region: "NI", code: "+505" },
  { region: "NE", code: "+227" },
  { region: "NG", code: "+234" },
  { region: "NU", code: "+683" },
  { region: "NF", code: "+672" },
  { region: "KP", code: "+850" },
  { region: "MK", code: "+389" },
  { region: "MP", code: "+1" },
  { region: "NO", code: "+47" },
  { region: "OM", code: "+968" },
  { region: "PK", code: "+92" },
  { region: "PW", code: "+680" },
  { region: "PS", code: "+970" },
  { region: "PA", code: "+507" },
  { region: "PG", code: "+675" },
  { region: "PY", code: "+595" },
  { region: "PE", code: "+51" },
  { region: "PH", code: "+63" },
  { region: "PL", code: "+48" },
  { region: "PT", code: "+351" },
  { region: "PR", code: "+1" },
  { region: "QA", code: "+974" },
  { region: "RE", code: "+262" },
  { region: "RO", code: "+40" },
  { region: "RU", code: "+7" },
  { region: "RW", code: "+250" },
  { region: "BL", code: "+590" },
  { region: "SH", code: "+290" },
  { region: "KN", code: "+1" },
  { region: "LC", code: "+1" },
  { region: "MF", code: "+590" },
  { region: "PM", code: "+508" },
  { region: "VC", code: "+1" },
  { region: "WS", code: "+685" },
  { region: "SM", code: "+378" },
  { region: "ST", code: "+239" },
  { region: "SA", code: "+966" },
  { region: "SN", code: "+221" },
  { region: "RS", code: "+381" },
  { region: "SC", code: "+248" },
  { region: "SL", code: "+232" },
  { region: "SG", code: "+65" },
  { region: "SX", code: "+1" },
  { region: "SK", code: "+421" },
  { region: "SI", code: "+386" },
  { region: "SB", code: "+677" },
  { region: "SO", code: "+252" },
  { region: "ZA", code: "+27" },
  { region: "KR", code: "+82" },
  { region: "SS", code: "+211" },
  { region: "ES", code: "+34" },
  { region: "LK", code: "+94" },
  { region: "SD", code: "+249" },
  { region: "SR", code: "+597" },
  { region: "SJ", code: "+47" },
  { region: "SE", code: "+46" },
  { region: "CH", code: "+41" },
  { region: "SY", code: "+963" },
  { region: "TA", code: "+290" },
  { region: "TW", code: "+886" },
  { region: "TJ", code: "+992" },
  { region: "TZ", code: "+255" },
  { region: "TH", code: "+66" },
  { region: "TL", code: "+670" },
  { region: "TG", code: "+228" },
  { region: "TK", code: "+690" },
  { region: "TO", code: "+676" },
  { region: "TT", code: "+1" },
  { region: "TN", code: "+216" },
  { region: "TR", code: "+90" },
  { region: "TM", code: "+993" },
  { region: "TC", code: "+1" },
  { region: "TV", code: "+688" },
  { region: "VI", code: "+1" },
  { region: "UG", code: "+256" },
  { region: "UA", code: "+380" },
  { region: "AE", code: "+971" },
  { region: "GB", code: "+44" },
  { region: "US", code: "+1" },
  { region: "UY", code: "+598" },
  { region: "UZ", code: "+998" },
  { region: "VU", code: "+678" },
  { region: "VA", code: "+39" },
  { region: "VE", code: "+58" },
  { region: "VN", code: "+84" },
  { region: "WF", code: "+681" },
  { region: "EH", code: "+212" },
  { region: "YE", code: "+967" },
  { region: "ZM", code: "+260" },
  { region: "ZW", code: "+263" },
];

function phoneCountryLabel(option: PhoneCountryOption) {
  const regionLabel = option.region === "GB" ? "UK" : option.region;
  return `${regionLabel} ${option.code}`;
}

// 提供与官网视觉一致、支持搜索和键盘操作的国家区号选择器。
function PhoneCountrySelect({
  value,
  onChange,
  phone,
  onPhoneChange,
  phonePlaceholder,
  phoneLabel,
  hasError,
  lang,
  labels,
}: {
  value: string;
  onChange: (region: string) => void;
  phone: string;
  onPhoneChange: (phone: string) => void;
  phonePlaceholder: string;
  phoneLabel: string;
  hasError: boolean;
  lang: Lang;
  // 香港版傳入繁體標籤；未傳時沿用英文/簡體預設。
  labels?: { country: string; search: string; empty: string };
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedOption = phoneCountryOptions.find((option) => option.region === value) || phoneCountryOptions[0];
  const sortedOptions = useMemo(
    () => [...phoneCountryOptions].sort((a, b) => phoneCountryLabel(a).localeCompare(phoneCountryLabel(b), "en")),
    [],
  );
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return sortedOptions;
    return sortedOptions.filter((option) => phoneCountryLabel(option).toLowerCase().includes(normalizedQuery));
  }, [query, sortedOptions]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);

  // 选中区号后关闭浮层，并把焦点交还给触发按钮。
  function selectOption(option: PhoneCountryOption) {
    onChange(option.region);
    setOpen(false);
    setQuery("");
    window.requestAnimationFrame(() => rootRef.current?.querySelector<HTMLButtonElement>("button")?.focus());
  }

  // 让搜索框同时承担列表的方向键与确认操作。
  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      window.requestAnimationFrame(() => rootRef.current?.querySelector<HTMLButtonElement>("button")?.focus());
      return;
    }
    if (!filteredOptions.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index) => (index + direction + filteredOptions.length) % filteredOptions.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectOption(filteredOptions[activeIndex]);
    }
  }

  const listboxId = "phone-country-listbox";
  const activeOptionId = filteredOptions[activeIndex] ? `phone-country-${filteredOptions[activeIndex].region}` : undefined;

  return (
    <div ref={rootRef} className="relative w-full">
      <div
        className={`flex h-[44px] overflow-hidden rounded-xl border bg-white transition-all focus-within:border-[#7b2fd0] focus-within:ring-4 focus-within:ring-[#7b2fd0]/10 ${
          hasError ? "border-[#ff7b9a] bg-[#fff7fa]" : "border-[#eaddea]"
        }`}
      >
        <button
          type="button"
          aria-label={labels?.country ?? t(lang, "lead.country")}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={() => {
            setOpen((current) => !current);
            setQuery("");
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
            }
          }}
          className="flex h-full w-[112px] flex-shrink-0 cursor-pointer items-center justify-between gap-2 border-r border-[#eaddea] bg-white px-3 text-[13px] font-extrabold text-[#2a2630] outline-none transition-colors duration-200 hover:bg-[#faf7fc] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7b2fd0]/60 sm:w-[118px]"
        >
          <span>{phoneCountryLabel(selectedOption)}</span>
          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-[#765e7b] transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        <input
          type="tel"
          value={phone}
          onChange={(event) => onPhoneChange(event.target.value)}
          aria-label={phoneLabel}
          className="h-full min-w-0 flex-1 border-0 bg-transparent px-3.5 text-[14px] font-semibold text-[#100f12] outline-none placeholder:text-[#9a879b]"
          placeholder={phonePlaceholder}
        />
      </div>

      {open ? (
        <div className="absolute left-0 top-[calc(100%+10px)] z-40 w-full min-w-[260px] overflow-hidden rounded-[18px] border border-[#e7d9eb] bg-white p-2 shadow-[0_24px_64px_rgba(46,22,152,.18)]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a768c]" aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={activeOptionId}
              aria-autocomplete="list"
              aria-label={labels?.search ?? (lang === "zh" ? "搜索国家或区号" : "Search country or calling code")}
              placeholder={labels?.search ?? (lang === "zh" ? "搜索国家或区号" : "Search country or code")}
              className="min-h-[42px] w-full rounded-xl border border-[#eaddea] bg-[#fcfafc] py-2 pl-9 pr-3 text-[13px] font-semibold text-[#2a2630] outline-none placeholder:text-[#a08da3] focus:border-[#7b2fd0] focus:ring-4 focus:ring-[#7b2fd0]/10"
            />
          </div>
          <div id={listboxId} role="listbox" aria-label={labels?.country ?? t(lang, "lead.country")} className="mt-2 max-h-[248px] overflow-y-auto overscroll-contain p-1">
            {filteredOptions.length ? filteredOptions.map((option, index) => {
              const selected = option.region === value;
              const active = index === activeIndex;
              return (
                <button
                  id={`phone-country-${option.region}`}
                  key={option.region}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectOption(option)}
                  className={`flex min-h-[40px] w-full cursor-pointer items-center justify-between rounded-[10px] px-3 text-left text-[13px] font-bold transition-colors duration-150 ${
                    selected ? "bg-[#f3ecfa] text-[#5b24c9]" : active ? "bg-[#faf6fc] text-[#2a2630]" : "text-[#5f5363] hover:bg-[#faf6fc] hover:text-[#2a2630]"
                  }`}
                >
                  <span>{phoneCountryLabel(option)}</span>
                  {selected ? <Check className="h-4 w-4 text-[#7b2fd0]" aria-hidden="true" /> : null}
                </button>
              );
            }) : (
              <p className="px-3 py-6 text-center text-[13px] font-semibold text-[#8a768c]">
                {labels?.empty ?? (lang === "zh" ? "没有匹配的国家或区号" : "No matching country or code")}
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function redactedCurrentUrl() {
  if (typeof window === "undefined") return "";

  try {
    const url = new URL(window.location.href);
    sensitiveUrlQueryKeys.forEach((key) => url.searchParams.delete(key));
    return url.toString();
  } catch {
    return window.location.href;
  }
}

/* 香港版聯絡頁（variant="hk"）的繁體中文文案：i18n 的 lead.* 只有英文/簡體，
   這裡整組覆寫，語法用詞對齊香港站首頁。 */
const hkLeadCopy: Record<string, string> = {
  "lead.title": "聯絡我們",
  "lead.sub": "告訴我們你的聯絡方法和業務場景，ClawEase 團隊會盡快聯絡你，確認渠道、系統和示範安排。",
  "lead.incentive": "完成產品示範，即可獲得 50 美元 Amazon 禮品卡。",
  "lead.name": "姓名",
  "lead.name.placeholder": "請輸入你的姓名",
  "lead.email": "電郵",
  "lead.email.placeholder": "name@company.com",
  "lead.phone": "電話號碼",
  "lead.country": "選擇國家或地區",
  "lead.country.search": "搜尋國家或區號",
  "lead.country.empty": "沒有匹配的國家或地區",
  "lead.phone.placeholder": "請輸入電話號碼（選填）",
  "lead.company": "公司",
  "lead.company.placeholder": "公司或機構名稱（選填）",
  "lead.note": "備註資料",
  "lead.note.placeholder": "可以簡單描述你的業務場景，例如渠道、系統、預約/查詢流程等。",
  "lead.defaultContent": "官網聯絡表格提交，希望了解 ClawEase 示範和接入方案。",
  "lead.privacy": "提交後，我們會盡快聯絡你。你的資料僅用於回覆查詢、安排示範及 ClawEase 購買/接入溝通，不會用於無關用途。",
  "lead.submit": "提交需求",
  "lead.submitting": "提交中...",
  "lead.success.title": "已收到需求",
  "lead.success.desc": "我們會盡快聯絡你，確認業務場景和示範安排。",
  "lead.error.name": "請填寫姓名",
  "lead.error.email": "請填寫有效電郵",
  "lead.error.phone": "請填寫有效電話號碼",
  "lead.error.submit": "提交失敗，可能是後端服務暫未配置。請稍後重試，或直接電郵聯絡 info@clawease.ai。",
};

// 渲染官网线索提交页，语言由路由传入，未指定时默认英文；variant="hk" 切换为香港版（繁體文案 + 香港站導覽/頁腳）。
export function LeadFormPage({ defaultLang = "en" as Lang, variant }: { defaultLang?: Lang; variant?: "hk" }) {
  const lang = defaultLang;
  const hongKong = variant === "hk";
  const text = (key: string) => (hongKong ? hkLeadCopy[key] ?? t(lang, key) : t(lang, key));
  const [searchParams] = useSearchParams();
  const incentiveId = searchParams.get("id")?.trim() || "";
  const showIncentive = Boolean(incentiveId);
  const conversionPlatform = resolveConversionPlatform(searchParams);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(searchParams.get("email") || searchParams.get("email_id") || "");
  const [phoneCountryRegion, setPhoneCountryRegion] = useState(hongKong ? "HK" : "US");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const queryEmail = searchParams.get("email") || searchParams.get("email_id");
    if (queryEmail) setEmail(queryEmail);
  }, [searchParams]);

  function validate() {
    const nextErrors: Record<string, string> = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigits = phone.replace(/\D/g, "");

    if (!name.trim()) nextErrors.name = text("lead.error.name");
    if (!email.trim() || !emailPattern.test(email.trim())) nextErrors.email = text("lead.error.email");
    if (phone.trim() && phoneDigits.length < 5) nextErrors.phone = text("lead.error.phone");

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function submitLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate()) return;

    if (website.trim()) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setSubmitError("");

    if (import.meta.env.DEV) {
      reportLeadConversion(conversionPlatform);
    }

    try {
      const source = searchParams.get("source") || "lead_form";
      const selectedPhoneCountry = phoneCountryOptions.find((option) => option.region === phoneCountryRegion) || phoneCountryOptions.find((option) => option.region === "US") || phoneCountryOptions[0];
      const trackingLines = leadTrackingKeys.flatMap((key) => {
        const value = searchParams.get(key)?.trim();
        return value ? [`${key}=${value}`] : [];
      });
      const content = [
        note.trim() || text("lead.defaultContent"),
        ...(incentiveId ? [`id=${incentiveId}`] : []),
        `source=${source}`,
        ...trackingLines,
        ...(conversionPlatform ? [`conversion_platform=${conversionPlatform}`] : []),
        `page=${redactedCurrentUrl()}`,
        `lang=${hongKong ? "zh-HK" : lang}`,
      ].join("\n");

      const payload = {
        name: name.trim(),
        mobile: phone.trim() ? `${selectedPhoneCountry.code} ${phone.trim()}` : "",
        email: email.trim(),
        company: company.trim(),
        content,
      };

      const response = await fetch(contactEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Lead submit failed: ${response.status}`);
      const result = (await response.json()) as ContactUsResponse;
      if (result.code !== 0) {
        throw new Error(result.msg || text("lead.error.submit"));
      }
      setStatus("success");
      if (!import.meta.env.DEV) {
        reportLeadConversion(conversionPlatform);
      }
    } catch (error) {
      setSubmitError(error instanceof Error && error.message ? error.message : text("lead.error.submit"));
      setStatus("error");
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f8f9fa] text-[#100f12] font-sans">
      <Seo
        lang={lang}
        page="contact"
        document={hongKong ? getHongKongContactSeoDocument() : undefined}
        documentLanguage={hongKong ? "zh-HK" : undefined}
      />
      {hongKong ? <HomeNav locale="zh-HK" /> : <Nav lang={lang} />}
      <main className="relative overflow-hidden px-5 pt-28 sm:px-10 lg:pt-32">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[560px]"
          style={{
            background:
              "radial-gradient(circle at 18% 2%, rgba(153, 108, 255, .22), transparent 30%), radial-gradient(circle at 82% 4%, rgba(233, 48, 201, .16), transparent 32%), linear-gradient(180deg, #fff 0%, rgba(248,249,250,.95) 72%, #f8f9fa 100%)",
          }}
        />

        <section className="relative mx-auto max-w-[980px] pb-24 lg:pb-32">
          <div className="text-center">
            <h1 className="mx-auto max-w-[820px] text-[36px] font-extrabold leading-[1.05] tracking-[-0.04em] text-[#100f12] sm:text-[50px] lg:text-[58px]">
              {text("lead.title")}
            </h1>
            <p className="mx-auto mt-4 max-w-[680px] text-[15px] font-semibold leading-7 text-[#775f78]">{text("lead.sub")}</p>
            {showIncentive ? (
              <div className="mx-auto mt-4 inline-flex max-w-full items-center justify-center rounded-full border border-[#ead6f5] bg-white/86 px-4 py-2 text-[13px] font-extrabold text-[#5223b1] shadow-[0_14px_34px_rgba(82,35,177,.08)]">
                {text("lead.incentive")}
              </div>
            ) : null}
          </div>

          <form
            onSubmit={submitLead}
            className="mx-auto mt-7 max-w-[900px] rounded-[24px] border border-[#eaddea] bg-white p-5 shadow-[0_24px_76px_rgba(46,22,152,.10)] sm:p-6"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <FieldLabel required>{text("lead.name")}</FieldLabel>
                <input value={name} onChange={(event) => setName(event.target.value)} className={inputClass(Boolean(errors.name))} placeholder={text("lead.name.placeholder")} />
                {errors.name ? <p className="mt-2 text-xs font-semibold text-[#d8325b]">{errors.name}</p> : null}
              </label>

              <label className="block">
                <FieldLabel required>{text("lead.email")}</FieldLabel>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className={inputClass(Boolean(errors.email))}
                  placeholder={text("lead.email.placeholder")}
                />
                {errors.email ? <p className="mt-2 text-xs font-semibold text-[#d8325b]">{errors.email}</p> : null}
              </label>

              <div className="block">
                <FieldLabel>{text("lead.phone")}</FieldLabel>
                <PhoneCountrySelect
                  value={phoneCountryRegion}
                  onChange={setPhoneCountryRegion}
                  phone={phone}
                  onPhoneChange={setPhone}
                  phonePlaceholder={text("lead.phone.placeholder")}
                  phoneLabel={text("lead.phone")}
                  hasError={Boolean(errors.phone)}
                  lang={lang}
                  labels={hongKong ? { country: hkLeadCopy["lead.country"], search: hkLeadCopy["lead.country.search"], empty: hkLeadCopy["lead.country.empty"] } : undefined}
                />
                {errors.phone ? <p className="mt-2 text-xs font-semibold text-[#d8325b]">{errors.phone}</p> : null}
              </div>

              <label className="block">
                <FieldLabel>{text("lead.company")}</FieldLabel>
                <input value={company} onChange={(event) => setCompany(event.target.value)} className={inputClass()} placeholder={text("lead.company.placeholder")} />
              </label>

              <label className="block sm:col-span-2">
                <FieldLabel>{text("lead.note")}</FieldLabel>
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  className={`${inputClass()} min-h-[96px] resize-none py-3`}
                  placeholder={text("lead.note.placeholder")}
                />
              </label>
            </div>

            <label className="hidden" aria-hidden="true">
              Website
              <input tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
            </label>

            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-[560px] text-[13px] font-semibold leading-6 text-[#8a768c]">{text("lead.privacy")}</p>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl px-7 text-[14px] font-extrabold text-white shadow-[0_18px_42px_rgba(120,35,177,.24)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                style={{ background: "linear-gradient(135deg, #5223b1 0%, #7823b1 48%, #e930c9 100%)" }}
              >
                {status === "submitting" ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {status === "submitting" ? text("lead.submitting") : text("lead.submit")}
              </button>
            </div>

            {status === "success" ? (
              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#a7f3d0] bg-[#ecfdf5] p-4 text-left">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#10a878]" />
                <div>
                  <div className="text-sm font-extrabold text-[#14532d]">{text("lead.success.title")}</div>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#3f6f55]">{text("lead.success.desc")}</p>
                </div>
              </div>
            ) : null}

            {status === "error" ? (
              <div className="mt-6 rounded-2xl border border-[#ffd5df] bg-[#fff7fa] p-4 text-sm font-semibold leading-6 text-[#9f2946]">
                {submitError || text("lead.error.submit")}
              </div>
            ) : null}
          </form>
        </section>
      </main>
      {hongKong ? (
        <div className="mt-auto">
          <HongKongFooter />
        </div>
      ) : (
        <div className="relative mt-auto overflow-hidden bg-[#f8f9fa]">
          <div
            className="pointer-events-none absolute inset-0 opacity-90"
            style={{
              background:
                "radial-gradient(circle at 24% 6%, rgba(153, 108, 255, .13), transparent 30%), radial-gradient(circle at 76% 14%, rgba(233, 48, 201, .10), transparent 28%), linear-gradient(180deg, rgba(248,249,250,.98) 0%, rgba(242,239,249,.92) 100%)",
            }}
          />
          <div className="relative">
            <Footer lang={lang} />
          </div>
        </div>
      )}
    </div>
  );
}
